// OpenRouter pricing formatting. The API has NO unit field, so the unit is
// inferred heuristically (verified 2026-10-09 against all 26 STT models and
// OpenRouter's own model pages):
//
// Text models (per-token): prompt = $/input-token, completion = $/output-token
//   → "$X / $Y per M tokens" (multiply by 1,000,000)
//   e.g. gpt-4o-transcribe prompt "0.0000025" completion "0.00001"
//   → "$2.50 / $10.00 per M tokens"
//
// Transcription models (MIXED units, distinguished by magnitude):
//   - completion > 0        → per-token model → "$X / $Y per M tokens"
//     (e.g. openai/gpt-4o-transcribe)
//   - prompt >= 0.01        → per-hour → "$X/hr"
//     (e.g. microsoft/mai-transcribe-2 "0.1" → "$0.10/hr";
//      mai-transcribe-1.5 "0.36" → "$0.36/hr")
//   - else                  → per-second → "$X/sec", trailing zeros trimmed,
//     up to 6 significant digits
//     (e.g. openai/whisper-1 "0.0001" → "$0.0001/sec";
//      elevenlabs/scribe-v2 "0.00003055" → "$0.0000306/sec";
//      deepgram/nova-3 "0.0000716666666667" → "$0.0000717/sec")
//
// Both zero → "Free".

/** Trim trailing zeros, keeping up to 6 significant digits. */
function trimTo6SigFigs(n: number): string {
  if (n === 0) return "0";
  // Round to 6 significant digits.
  const rounded = Number(n.toPrecision(6));
  // Trim trailing zeros after the decimal point (and a bare trailing dot).
  return String(rounded).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

export function formatPricing(
  pricing: { prompt: string; completion: string },
  isTranscription: boolean,
): string {
  const prompt = parseFloat(pricing.prompt);
  const completion = parseFloat(pricing.completion);
  const perMInput = prompt * 1_000_000;
  const perMOutput = completion * 1_000_000;
  // Per-token display (text models AND per-token STT models like
  // gpt-4o-transcribe, which have a completion price).
  if (!isTranscription || completion > 0) {
    if (perMInput === 0 && perMOutput === 0) return "Free";
    return `$${perMInput.toFixed(2)} / $${perMOutput.toFixed(2)} per M tokens`;
  }
  // Transcription, no completion price → hourly or per-second by magnitude.
  if (prompt === 0) return "Free";
  // Per-hour: any per-second price is far below 1 cent, so >= $0.01 is hourly.
  if (prompt >= 0.01) {
    return `$${prompt.toFixed(2)}/hr`;
  }
  // Per-second.
  return `$${trimTo6SigFigs(prompt)}/sec`;
}
